import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { OrgProvider } from './context/OrgContext';
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import ResponsiveStyles from './components/ResponsiveStyles';
import { I18nProvider } from './i18n';
import InstallBanner from './components/InstallBanner';
import Login from './pages/Login';

// Lazy-loaded pages — split into separate chunks
const CaptureReading = lazy(() => import('./pages/CaptureReading'));
const ReadingsList = lazy(() => import('./pages/ReadingsList'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const UsersManagement = lazy(() => import('./pages/UsersManagement'));
const ZonesManagement = lazy(() => import('./pages/ZonesManagement'));
const MetersManagement = lazy(() => import('./pages/MetersManagement'));
const ConsumersManagement = lazy(() => import('./pages/ConsumersManagement'));
const ReportFailure = lazy(() => import('./pages/ReportFailure'));
const ReportsManagement = lazy(() => import('./pages/ReportsManagement'));
const ExportPage = lazy(() => import('./pages/ExportPage'));
const UnreadMeters = lazy(() => import('./pages/UnreadMeters'));
const MapPage = lazy(() => import('./pages/MapPage'));
const Pumps = lazy(() => import('./pages/Pumps'));
const Settings = lazy(() => import('./pages/Settings'));
const KvaroviInstalater = lazy(() => import('./pages/KvaroviInstalater'));
const ReportsDashboard = lazy(() => import('./pages/ReportsDashboard'));
const BillingDashboard = lazy(() => import('./pages/BillingDashboard'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const BillingExport = lazy(() => import('./pages/BillingExport'));
const PortalPage = lazy(() => import('./pages/PortalPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const AccountingPage = lazy(() => import('./pages/AccountingPage'));
const PurchaseInvoicesPage = lazy(() => import('./pages/PurchaseInvoicesPage'));
const VATReturnPage = lazy(() => import('./pages/VATReturnPage'));
const KarticaPage = lazy(() => import('./pages/KarticaPage'));
const KasaPage = lazy(() => import('./pages/KasaPage'));
const IzvjestajiPage = lazy(() => import('./pages/IzvjestajiPage'));
const FinancialReportsPage = lazy(() => import('./pages/FinancialReportsPage'));
const AnnouncementGenerator = lazy(() => import('./pages/AnnouncementGenerator'));

function PageLoader() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#0c4a6e',
          borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          margin: '0 auto 12px',
        }} />
        <span style={{ color: '#64748b', fontSize: 14 }}>Učitavanje...</span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/admin/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin" />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/admin/login" />;
  if (user.role === 'inkasant') return <CaptureReading />;
  if (user.role === 'vodoinstalater') return <KvaroviInstalater />;
  if (user.role === 'obracun') return <BillingDashboard />;
  if (user.role === 'kasa') return <KasaPage />;
  return <Dashboard />;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <>
      {user && <Navigation />}
      {user && <InstallBanner />}
      <div style={{ paddingTop: user ? 56 : 0, minHeight: '100vh', background: '#f8fafc' }}>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="login" element={<Login />} />
              <Route path="" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />

              {/* Inkasant */}
              <Route path="my-readings" element={<ProtectedRoute roles={['inkasant']}><ReadingsList /></ProtectedRoute>} />
              <Route path="report" element={<ProtectedRoute roles={['inkasant']}><ReportFailure /></ProtectedRoute>} />

              {/* Kontrolor + Admin */}
              <Route path="readings" element={<ProtectedRoute roles={['kontrolor', 'admin']}><ReadingsList /></ProtectedRoute>} />
              <Route path="reports" element={<ProtectedRoute roles={['kontrolor', 'admin']}><ReportsManagement /></ProtectedRoute>} />
              <Route path="export" element={<ProtectedRoute roles={['kontrolor', 'admin']}><ExportPage /></ProtectedRoute>} />
              <Route path="reports-dashboard" element={<ProtectedRoute roles={['kontrolor', 'admin']}><ReportsDashboard /></ProtectedRoute>} />

              {/* Kontrolor + Admin + Inkasant */}
              <Route path="unread" element={<ProtectedRoute><UnreadMeters /></ProtectedRoute>} />

              {/* Kontrolor + Admin */}
              <Route path="map" element={<ProtectedRoute roles={['kontrolor', 'admin']}><MapPage /></ProtectedRoute>} />
              <Route path="readings-map" element={<Navigate to="/admin/map" />} />

              {/* Vodoinstalater */}
              <Route path="kvarovi" element={<ProtectedRoute roles={['vodoinstalater', 'admin']}><KvaroviInstalater /></ProtectedRoute>} />

              {/* Obracun + Admin */}
              <Route path="invoices" element={<ProtectedRoute roles={['obracun', 'admin']}><InvoicesPage /></ProtectedRoute>} />
              <Route path="payments" element={<ProtectedRoute roles={['obracun', 'admin']}><PaymentsPage /></ProtectedRoute>} />
              <Route path="billing-export" element={<ProtectedRoute roles={['obracun', 'admin']}><BillingExport /></ProtectedRoute>} />
              <Route path="kartica" element={<ProtectedRoute roles={['admin', 'obracun', 'kasa']}><KarticaPage /></ProtectedRoute>} />
              <Route path="izvjestaji" element={<ProtectedRoute roles={['admin', 'obracun']}><IzvjestajiPage /></ProtectedRoute>} />

              {/* Kasa */}
              <Route path="kasa" element={<ProtectedRoute roles={['admin', 'kasa']}><KasaPage /></ProtectedRoute>} />

              {/* Admin only */}
              <Route path="pumps" element={<ProtectedRoute roles={['admin']}><Pumps /></ProtectedRoute>} />
              <Route path="users" element={<ProtectedRoute roles={['admin']}><UsersManagement /></ProtectedRoute>} />
              <Route path="zones" element={<ProtectedRoute roles={['admin']}><ZonesManagement /></ProtectedRoute>} />
              <Route path="meters" element={<ProtectedRoute roles={['admin']}><MetersManagement /></ProtectedRoute>} />
              <Route path="consumers" element={<ProtectedRoute roles={['admin']}><ConsumersManagement /></ProtectedRoute>} />
              <Route path="partners" element={<ProtectedRoute roles={['admin', 'obracun']}><PartnersPage /></ProtectedRoute>} />
              <Route path="ledger" element={<ProtectedRoute roles={['admin', 'obracun']}><AccountingPage /></ProtectedRoute>} />
              <Route path="purchase-invoices" element={<ProtectedRoute roles={['admin', 'obracun']}><PurchaseInvoicesPage /></ProtectedRoute>} />
              <Route path="vat-return" element={<ProtectedRoute roles={['admin', 'obracun']}><VATReturnPage /></ProtectedRoute>} />
              <Route path="financial-reports" element={<ProtectedRoute roles={['admin', 'obracun']}><FinancialReportsPage /></ProtectedRoute>} />
              <Route path="announcements" element={<ProtectedRoute roles={['admin', 'kontrolor']}><AnnouncementGenerator /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute roles={['admin']}><Settings /></ProtectedRoute>} />

              {/* 404 */}
              <Route path="*" element={<Navigate to="/admin" />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider><ToastProvider><OrgProvider><AuthProvider>
        <ResponsiveStyles />
        <Routes>
          {/* Portal — standalone at root, no layout/sidebar */}
          <Route path="/" element={
            <Suspense fallback={<PageLoader />}><PortalPage /></Suspense>
          } />
          {/* Backward compat: old /portal URL redirects to / */}
          <Route path="/portal" element={<Navigate to="/" replace />} />
          {/* Admin/management routes */}
          <Route path="/admin/*" element={<AppRoutes />} />
          {/* Everything else → portal */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider></OrgProvider></ToastProvider></I18nProvider>
    </BrowserRouter>
  );
}
